import { config } from "@/config";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { useCallback } from "react";

export interface NodeCountResult {
  architecturalId?: string;
  functionalId?: string;
  designId?: string;
  nestedNodeCount: number;
}

export interface NodeCounts {
  functional: {
    total: number;
    details: NodeCountResult[];
  };
  architecture: {
    total: number;
    details: NodeCountResult[];
  };
  design: {
    total: number;
    details: NodeCountResult[];
  };
  code: number;
  repositories: number;
  linesOfCode: number;
  updatedAt: string | null;
}

interface NodeCountsApiResponse {
  counts: {
    functional?: number;
    design?: number;
    architecture?: number;
    code?: number;
    repositories?: number;
    linesOfCode?: number;
  };
  updatedAt: string | null;
  cached: boolean;
}

const buildNodeCounts = (body: NodeCountsApiResponse): NodeCounts => {
  const counts = body.counts || {};
  return {
    functional: { total: counts.functional ?? 0, details: [] },
    architecture: { total: counts.architecture ?? 0, details: [] },
    design: { total: counts.design ?? 0, details: [] },
    code: counts.code ?? 0,
    repositories: counts.repositories ?? 0,
    linesOfCode: counts.linesOfCode ?? 0,
    updatedAt: body.updatedAt,
  };
};

export const NODE_COUNTS_QUERY_KEY_ROOT = "node-counts" as const;

export const fetchNodeCounts = async (
  projectUuid: string,
  headers: Record<string, string>,
  refresh: boolean,
): Promise<NodeCounts> => {
  const url = `${config.isometricApiUrl}/projects/${projectUuid}/node-counts${refresh ? "?refresh=true" : ""}`;
  const response = await apiFetch(url, { method: "GET", headers });

  if (!response.ok) {
    throw new Error(
      `Node counts request failed: ${response.status} ${response.statusText}`,
    );
  }

  return buildNodeCounts((await response.json()) as NodeCountsApiResponse);
};

export const useGetNodeCounts = (
  projectUuid: string,
  enabled: boolean = true,
) => {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  const queryKey = [NODE_COUNTS_QUERY_KEY_ROOT, projectUuid];

  const query = useQuery({
    queryKey,
    queryFn: (): Promise<NodeCounts> =>
      fetchNodeCounts(projectUuid, getAuthHeaders(), false),
    enabled: enabled && !!projectUuid,
    // Re-fetched on each mount / focus / reconnect once 10 s old. Backend's own
    // 10-min Postgres cache keeps this cheap (~5 ms), and drops the "dashboard
    // shows stale count for up to 5 min" UX regression; the 10 s stops a second
    // request when the dashboard and its header mount one after the other
    // (BREEZEAI-1471 BUG-6). Refresh still forces a recount.
    staleTime: 10_000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const refresh = useCallback(async () => {
    const fresh = await fetchNodeCounts(projectUuid, getAuthHeaders(), true);
    queryClient.setQueryData(queryKey, fresh);
    return fresh;
  }, [projectUuid, getAuthHeaders, queryClient]);

  return { ...query, refresh };
};
